import { useEffect, useState } from 'react';
import { authApi, type AuthUser } from '../api/auth';
import { Wordmark } from '../Wordmark';

/**
 * Two ways in, matched to where you're standing:
 *
 * - **Password** (default): the credential for web/desktop access, including the
 *   internet-facing admin host. A 4-digit PIN is only ~10k combinations, which
 *   is too weak to expose publicly.
 * - **PIN + account picker**: fast entry on a shop-floor iPad/tablet — tap your
 *   name, tap your PIN. Only offered when the deployment enables it
 *   (LOGIN_PIN_ENABLED), and auto-selected on touch devices.
 */
export function LoginView({
  onSignedIn,
  onBack,
  environment = 'production',
  environmentLabel,
}: {
  onSignedIn: () => void;
  onBack?: () => void;
  environment?: string;
  environmentLabel?: string;
}) {
  // The moment before signing in is the last one where telling the two systems
  // apart is free, so this is where the marking has to be loudest.
  const isStaging = environment !== 'production';
  // The demo keeps the environment NOTICE but not staging's dark warning chrome:
  // its ZeroIncidents skin owns the look, so the sign-in sits on the light surface.
  const isDemo = environment === 'demo';
  const [accounts, setAccounts] = useState<AuthUser[]>([]);
  const [pinEnabled, setPinEnabled] = useState(false);
  const [pickerEnabled, setPickerEnabled] = useState(false);
  const [mode, setMode] = useState<'password' | 'pin'>('password');

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    authApi.users().then((r) => {
      setAccounts(r.accounts);
      setPinEnabled(r.pinEnabled);
      setPickerEnabled(r.pickerEnabled);
      if (r.accounts.length > 0) setName((cur) => cur || r.accounts[0]!.name);
      // Touch-primary device (iPad/tablet) => default to the keypad.
      const touch = window.matchMedia?.('(pointer: coarse)').matches ?? false;
      if (r.pinEnabled && touch) setMode('pin');
    });
  }, []);

  const submit = async () => {
    const credential = mode === 'pin' ? { pin } : { password };
    if (!name || (mode === 'pin' ? pin.length < 3 : password.length < 1)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authApi.login(name, credential);

      /**
       * The credentials were accepted. Now check the session actually stuck,
       * because a Secure cookie over plain HTTP is discarded by the browser
       * silently — the app would bounce straight back to this screen looking
       * like a rejected password, and retrying never helps.
       *
       * Measured, not predicted. The server can only guess at the transport
       * from X-Forwarded-Proto, and a reverse proxy that omits that header
       * would make it cry wolf on a perfectly good HTTPS deployment — blocking
       * a login that works. Asking who we are is the ground truth.
       */
      const check = await authApi.me();
      if (!check.user) {
        setError(
          res.warning ??
            'Signed in, but the session was not kept by your browser. This usually ' +
              'means the site is being served over plain HTTP while the session ' +
              'cookie is marked Secure.',
        );
        return;
      }
      onSignedIn();
    } catch (e) {
      setError((e as Error).message);
      setPin('');
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  const press = (digit: string) => setPin((p) => (p + digit).slice(0, 8));

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isStaging && !isDemo ? '#1c1207' : 'var(--bg)',
      }}
    >
      <div
        className="panel"
        style={{
          width: 420,
          padding: 32,
          ...(isStaging && !isDemo
            ? {
                border: '2px solid #f97316',
                borderTop: '10px solid #f97316',
                boxShadow: '0 0 0 1px #7c2d12, 0 24px 60px rgba(0,0,0,.6)',
              }
            : {}),
        }}
      >
        <div className="brand" style={{ padding: '0 0 20px', fontSize: 28 }}>
          <Wordmark />
        </div>

        {isStaging && (
          <div
            style={{
              margin: '0 0 22px',
              padding: '14px 16px',
              borderRadius: 8,
              background: 'rgba(249,115,22,.12)',
              border: '1px solid rgba(249,115,22,.45)',
            }}
          >
            <div
              style={{
                color: '#fdba74',
                font: '700 13px/1 ui-sans-serif, system-ui, sans-serif',
                letterSpacing: '.16em',
                marginBottom: 8,
              }}
            >
              ⚠ {environment === 'staging' ? 'STAGING' : environment.toUpperCase()} ENVIRONMENT
            </div>
            <div style={{ color: '#fed7aa', fontSize: 13, lineHeight: 1.55 }}>
              {environmentLabel ||
                'This is not the production system. Inspections, models and invoices here are test data and can be wiped without notice.'}
            </div>
            <div style={{ color: '#fdba74', fontSize: 12, marginTop: 8, opacity: 0.8 }}>
              {window.location.host}
            </div>
          </div>
        )}

        <label>Who are you?</label>
        {mode === 'pin' && pickerEnabled && accounts.length > 0 ? (
          <select value={name} onChange={(e) => setName(e.target.value)} style={{ width: '100%' }}>
            {accounts.map((u) => (
              <option key={u.id} value={u.name}>
                {u.name} ({u.role})
              </option>
            ))}
          </select>
        ) : (
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Account name"
            autoComplete="username"
            style={{ width: '100%' }}
            onKeyDown={(e) => e.key === 'Enter' && void submit()}
          />
        )}

        {mode === 'password' ? (
          <>
            <label style={{ marginTop: 20 }}>Password</label>
            <input
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void submit()}
              style={{ width: '100%' }}
            />
            <button
              className="btn primary"
              style={{ width: '100%', marginTop: 18, padding: 14, fontSize: 17 }}
              disabled={busy || !name || !password}
              onClick={() => void submit()}
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </>
        ) : (
          <>
            <label style={{ marginTop: 20 }}>PIN</label>
            <div
              style={{
                fontSize: 34,
                letterSpacing: 12,
                fontFamily: 'var(--mono)',
                textAlign: 'center',
                padding: '12px 0',
                minHeight: 58,
                background: 'var(--bg)',
                borderRadius: 8,
                border: '1px solid var(--border)',
              }}
            >
              {'•'.repeat(pin.length)}
            </div>
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 16 }}
            >
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <button
                  key={d}
                  className="btn"
                  style={{ fontSize: 24, padding: 18 }}
                  onClick={() => press(d)}
                >
                  {d}
                </button>
              ))}
              <button className="btn" style={{ fontSize: 16, padding: 18 }} onClick={() => setPin('')}>
                Clear
              </button>
              <button className="btn" style={{ fontSize: 24, padding: 18 }} onClick={() => press('0')}>
                0
              </button>
              <button
                className="btn primary"
                style={{ fontSize: 20, padding: 18 }}
                disabled={busy || pin.length < 3}
                onClick={() => void submit()}
              >
                {busy ? '…' : '→'}
              </button>
            </div>
          </>
        )}

        {/* Only offer the kiosk path where the deployment allows it. */}
        {pinEnabled && (
          <button
            className="btn sm"
            style={{ width: '100%', marginTop: 14 }}
            onClick={() => {
              setMode((m) => (m === 'pin' ? 'password' : 'pin'));
              setError(null);
              setPin('');
              setPassword('');
            }}
          >
            {mode === 'pin' ? 'Use password instead' : 'Use PIN (tablet)'}
          </button>
        )}

        {error && (
          <div style={{ color: 'var(--bad)', marginTop: 14, textAlign: 'center' }}>{error}</div>
        )}

        {onBack && (
          <button
            className="btn sm"
            style={{ width: '100%', marginTop: 14, background: 'transparent', border: 'none' }}
            onClick={onBack}
          >
            ← Back
          </button>
        )}
      </div>
    </div>
  );
}
